import type { ReactNode } from 'react'
import React from 'react'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isString from 'lodash/isString'
import { PivotViewer as PivotViewerCore } from '../PivotViewer'
import { FixedPage, Page } from '@ztechapps/webui.page'
import { FilterMenu } from '@ztechapps/webui.data-tools'
import { jsonTryParse } from '@ztechapps/common.utils'
import { HorizontalStack } from '@ztechapps/webui.util'
import { PivotBreadcrumbs } from '../PivotBreadcrumbs'
import type { ListField } from '@ztechapps/common.hook'
import type { BreadcrumbItem } from '@ztechapps/webui.page'
import {
  showAllFilterTemplate,
  useDeepCompareEffect,
  useInitialFilterTemplate,
  useLazyTemplateDocument,
  useReactiveState,
} from '@ztechapps/common.hook'
import type { PivotCellRenderer, PivotCellTextRenderer } from '../constants/types'
import type { Template } from '@ztechapps/common.contract'

export type PivotPageProps = {
  breadcrumbs?: BreadcrumbItem[]
  initialFilter?: Record<any, any>
  templateCategory?: string
  name?: string
  filterCategory?: string
  filterFields?: ListField[]
  actions?: ReactNode
  permissionRoleName?: string
  renderCell?: PivotCellRenderer
  renderCellText?: PivotCellTextRenderer
  title?: ReactNode
  siteName?: string
  refreshTrigger?: number
}
export const PivotPage = ({
  breadcrumbs,
  initialFilter = {},
  templateCategory,
  name,
  filterCategory,
  filterFields,
  actions,
  permissionRoleName = 'reports.canView',
  renderCell,
  renderCellText,
  title,
  siteName,
  refreshTrigger,
}: PivotPageProps) => {
  const [template, setTemplate] = React.useState(null)
  const [filter, setFilter] = useReactiveState(initialFilter)
  // The header filter applied last, by the menu or a programmatic switch — mirrored back
  // into the menu so its label tracks switches that bypass the menu's own click path.
  const [appliedHeaderTemplate, setAppliedHeaderTemplate] = React.useState<Template | undefined>(undefined)

  const { filterTemplates, initialFilterTemplate } = useInitialFilterTemplate({ category: filterCategory })
  useDeepCompareEffect(() => {
    onFilter({ template: initialFilterTemplate })
  }, [initialFilterTemplate])

  // the header filter names travel with the open-pivot bridge, so the chat can name one to pick —
  // the reload-all pseudo-template is not a saved filter, so it stays out of the names
  const headerFilters = React.useMemo(
    () =>
      (filterTemplates ?? [])
        .filter((template) => template._id !== showAllFilterTemplate._id)
        .map((template) => template.name)
        .filter((name) => !isEmpty(name)),
    [filterTemplates]
  )

  const onFilter = ({ template }: { template: Template }) => {
    setAppliedHeaderTemplate(template ?? undefined)
    //only merge the reportFilter if it has value.
    //because it re-render the pivot even when the reportFilter is empty. It set it to  {}
    const reportFilter = jsonTryParse(template?.value)

    //just use the default filter if the reportFilter is null
    if (isEmpty(reportFilter)) {
      setFilter(initialFilter)
    } else {
      setFilter({ reportFilter, ...initialFilter })
    }
  }

  // Programmatic entry to the same header-filter chain the filter menu drives —
  // the name must equal a template name exactly. An unknown name leaves the
  // pivot untouched instead of falling back to the initial filter.
  const switchHeaderFilter = (headerFilter: string) => {
    const match = (filterTemplates ?? []).find(({ name }) => name === headerFilter)
    if (match) onFilter({ template: match })
  }

  // A pivot template that auto-applies a linked filter template: load that template, then take the same
  // path the filter menu takes, so the menu label follows it. Each open may name a different template, and one
  // may have been edited since — always read it fresh.
  const { executeQuery: loadFilterTemplate } = useLazyTemplateDocument({
    filter: {},
    options: { queryOptions: { fetchPolicy: 'network-only', nextFetchPolicy: 'network-only' } },
  })
  const onApplyFilterTemplate = async (filterTemplateId: string) => {
    // The sentinel has no backing document — querying for it returns nothing, so take the same unfiltered
    // path the filter menu's own mount-time initialization already takes instead of round-tripping it.
    if (filterTemplateId === showAllFilterTemplate._id) {
      onFilter({ template: showAllFilterTemplate })
      return
    }
    const { data, error } = await loadFilterTemplate({ variables: { filter: { _id: filterTemplateId } } })
    if (error) return
    // the lazy executor types `data` as the entity, but it holds the query's `singleTemplate` field
    const linkedTemplate: Template | undefined = get(data, 'singleTemplate')
    if (linkedTemplate) onFilter({ template: linkedTemplate })
  }

  const onChangeTemplate = React.useCallback(
    ({ template }) => {
      setTemplate(template)
    },
    [setTemplate]
  )

  const filterAction = isEmpty(filterFields) ? null : (
    <FilterMenu
      {...{
        onFilter,
        category: filterCategory,
        filterFields,
        appliedTemplate: appliedHeaderTemplate,
      }}
    />
  )

  // Filter and page actions pin in the title band — the viewer toolbar below
  // scrolls away with the body, so anything that must stay reachable while
  // scrolling lives in the header slot instead.
  const headerActions =
    filterAction || actions ? (
      <HorizontalStack gap={2}>
        {filterAction}
        {actions}
      </HorizontalStack>
    ) : undefined

  return (
    <Page roleName={permissionRoleName}>
      <FixedPage.Header
        title={title}
        siteName={siteName}
        breadcrumbs={<PivotBreadcrumbs crumbs={breadcrumbs} template={template} />}
        actions={headerActions}
      />
      <FixedPage.Body>
        {/*we weill load only the pivot code when filter, and template are ready*/}

        <PivotViewerCore
          {...{
            name,
            templateCategory,
            onChangeTemplate,
            filter,
            renderCell,
            renderCellText,
            refreshTrigger,
            exportFileName: isString(title) ? title : name,
            headerFilters,
            filterCategory,
            onApplyFilterTemplate,
            switchHeaderFilter,
          }}
        />
      </FixedPage.Body>
    </Page>
  )
}
